import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { loginState } from 'global-stores';
import { Kebab, PageHead, Pager, SearchBox, Tabs } from 'views/design-system';
import { SignedDocPacketsState, useSignedDocsPacketsInbox } from 'views/signed-doc-packets-inbox';
import { ISignedDocInbox, useSignedDocsInbox } from 'views/signed-doc-inbox';

import {
	CHIP,
	EDGE,
	TYPE_LABEL,
	actionOf,
	dayOf,
	explain,
	pageCount,
	timeOf,
} from '../esign-deck/derive';
import { EnvelopeDrawer } from '../esign-deck/envelope-drawer';
import { useEnvelopeSigning } from '../esign-deck/use-envelope-signing';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './esign-inbox-deck.scss';

const PAGE_SIZE = 15;

type ITab = 'needToSign' | 'waitingForOthers' | 'completed';

const TABS: { key: ITab; label: string }[] = [
	{ key: 'needToSign', label: 'Need to Sign' },
	{ key: 'waitingForOthers', label: 'Waiting for Others' },
	{ key: 'completed', label: 'Completed' },
];

/**
 * The received-side mirror of esign-deck: same row shape, same derive.ts
 * helpers (actionOf already computes state from a given email's perspective
 * as a recipient, which is exactly this page's frame), different data source
 * (action=received, the packets-inbox hook — a genuinely different query
 * esign-deck's own table cannot surface) and no sender-only settings
 * (identity strip / Start-now belong to the sender, not here).
 */
export const EsignInboxDeck = () => {
	const { email } = useRecoilValue(loginState) ?? {};
	const { data: envelopes, isLoaded } = useRecoilValue(SignedDocPacketsState);
	const { getSignedDocsPacketsInbox } = useSignedDocsPacketsInbox();
	const { resendSignDocInbox, downloadSignDocInbox } = useSignedDocsInbox();

	const [tab, setTab] = useState<ITab>('needToSign');
	const [typed, setTyped] = useState('');
	const [page, setPage] = useState(1);
	const [open, setOpen] = useState<ISignedDocInbox | null>(null);
	const [busyRow, setBusyRow] = useState('');

	const reload = useCallback(() => {
		getSignedDocsPacketsInbox();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		reload();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const decorated = useMemo(
		() =>
			(envelopes ?? []).map((env: ISignedDocInbox) => {
				const action = actionOf(env, email);
				return { env, action, chip: CHIP[action], why: explain(env, action) };
			}),
		[envelopes, email]
	);

	const counts = useMemo(() => {
		const by = (a: string) => decorated.filter(d => d.action === a).length;
		return {
			needToSign: by('needToSign'),
			waitingForOthers: by('waitingForOthers'),
			completed: by('completed'),
		};
	}, [decorated]);

	const filtered = useMemo(() => {
		const q = typed.trim().toLowerCase();
		return decorated.filter(({ env, action }) => {
			if (action !== tab) return false;
			if (!q) return true;
			const haystack = [
				env.name,
				env.sender?.fullName ?? env.sender?.name,
				env.sender?.email,
				...(env.recipients ?? []).map(r => `${r.fullName ?? r.name} ${r.email}`),
			]
				.join(' ')
				.toLowerCase();
			return haystack.includes(q);
		});
	}, [decorated, tab, typed]);

	const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const swapTab = useCallback((k: string) => {
		setTab(k as ITab);
		setPage(1);
	}, []);

	/* This is the screen a recipient actually lands on, and its Sign button used
	   to open the drawer — the SAME launcher the sent-side deck uses. */
	const { startSigning, signModal } = useEnvelopeSigning({
		email,
		reload,
		setBusyRow,
		onNoRecipient: setOpen,
	});

	/** Recipient-framed: resend doesn't apply to yourself, so waitingForOthers
	 * just opens the drawer rather than offering a sender-only action. */
	const primaryAction = useCallback(
		(env: ISignedDocInbox, action: string) => {
			const busy = () => setBusyRow(env._id);
			const done = () => setBusyRow('');
			switch (action) {
				case 'completed':
					return { label: 'Download', run: () => { busy(); downloadSignDocInbox(env._id, done); } };
				case 'needToSign':
					return { label: 'Sign', run: () => startSigning(env) };
				default:
					return { label: 'Open', run: () => setOpen(env) };
			}
		},
		[downloadSignDocInbox, startSigning]
	);

	const kebabFor = useCallback(
		(env: ISignedDocInbox, action: string) => {
			const items: { label: string; onClick: () => void }[] = [
				{ label: 'Open details', onClick: () => setOpen(env) },
				{
					label: 'Download signed PDF',
					onClick: () => {
						setBusyRow(env._id);
						downloadSignDocInbox(env._id, () => setBusyRow(''));
					},
				},
			];
			// Only a packet actually waiting on this user can be signed, so the
			// item is absent rather than disabled everywhere else.
			if (action === 'needToSign') {
				items.unshift({ label: 'Sign', onClick: () => startSigning(env) });
			}
			return items;
		},
		[downloadSignDocInbox, startSigning]
	);
	// resendSignDocInbox is reused by EnvelopeDrawer's own actions when opened
	// from this page; kept in scope here rather than re-imported there.
	void resendSignDocInbox;

	return (
		<div className="sv2-scope sv2 esign-inbox-deck">
			<PageHead
				title="Received"
				sub={
					<>
						{counts.needToSign + counts.waitingForOthers} packets waiting on your
						signature or response
					</>
				}
			/>

			<div className="ctl">
				<SearchBox
					value={typed}
					onChange={v => {
						setTyped(v);
						setPage(1);
					}}
					placeholder="Search subject or sender"
					title="Searches the subject, sender and every recipient's name and email"
				/>
				<Pager page={page} pages={pages} onChange={setPage} />
			</div>

			<Tabs
				tabs={TABS.map(t => ({ ...t, count: counts[t.key] }))}
				active={tab}
				onChange={swapTab}
			/>

			<div className="panel" style={{ marginTop: 12 }}>
				<div className="tscroll">
					<table className="stbl etbl">
						<thead>
							<tr>
								<th className="subcol">Subject</th>
								<th className="stcol">Status</th>
								<th>WorkFlow</th>
								<th>Type</th>
								<th>Face Auth.</th>
								<th>Created At</th>
								<th className="accol">Actions</th>
							</tr>
						</thead>
						<tbody>
							{shown.map(({ env, action, chip, why }) => (
								<tr
									key={env._id}
									className={`${EDGE[action]}${open?._id === env._id ? ' sel' : ''}`}
									onClick={() => setOpen(env)}
									title="Open envelope details"
								>
									<td className="od subcol">
										<span className="nm">{env.name || 'Untitled packet'}</span>
										<br />
										{(() => {
											const sender = env.sender?.fullName || env.sender?.name || env.sender?.email;
											// env.sender is unpopulated on the vast majority of real envelopes
											// (verified in mongo, not a local-seed gap) — drop the line rather
											// than print "From unknown sender", same convention as val() in
											// session-drawer.tsx for other vendor fields that come back empty.
											return sender ? (
												<>
													<span className="mail">From {sender}</span>
													<br />
												</>
											) : null;
										})()}
										<span className="fund">
											{env.documents?.length ?? 0} document
											{(env.documents?.length ?? 0) === 1 ? '' : 's'} &middot;{' '}
											{pageCount(env)} pages
										</span>
									</td>

									<td className="fl stcol">
										<span className={`vch ${chip.cls}`}>{chip.label}</span>
										<div className={`wline${why.tone ? ` ${why.tone}` : ''}`}>{why.text}</div>
									</td>

									<td className="mid">{env.purpose ?? '--'}</td>
									<td className="mid">
										{TYPE_LABEL[env.purpose as string] ?? env.origin ?? '--'}
									</td>
									<td className="mid">
										<span
											className={`auth-chip${
												env.authType === 'facial' ? ' fac' : env.authType ? '' : ' none'
											}`}
										>
											{env.authType ?? '--'}
										</span>
									</td>
									<td className="mid">
										{dayOf(env.createdAt)}
										<span className="fund">{timeOf(env.createdAt)}</span>
									</td>
									<td className="accol">
										{(() => {
											const primary = primaryAction(env, action);
											return (
												<button
													className="reinv"
													type="button"
													disabled={busyRow === env._id}
													onClick={e => {
														e.stopPropagation();
														primary.run();
													}}
												>
													{busyRow === env._id ? '…' : primary.label}
												</button>
											);
										})()}
										<Kebab items={kebabFor(env, action)} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{!isLoaded && <div className="sv2-loading">Loading envelopes…</div>}
				{isLoaded && filtered.length === 0 && (
					<div className="sv2-empty">No envelopes match this filter.</div>
				)}
			</div>

			<div className="foot">
				A packet blocked on you always names the exact tabs it&rsquo;s waiting on
				&mdash; never a bare &ldquo;Waiting for others&rdquo;. Click a row to open the
				envelope.
			</div>

			<EnvelopeDrawer
				envelope={open}
				open={!!open}
				onClose={() => setOpen(null)}
				onChanged={reload}
				onSign={env => {
					// The drawer is showing the envelope as it was before signing, so
					// it is dismissed rather than left behind the widget to go stale.
					setOpen(null);
					startSigning(env);
				}}
			/>

			{signModal}
		</div>
	);
};

export default EsignInboxDeck;
